import { Box, Button, FormControl, Input, Option, Select, Sheet, Typography } from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import ApartmentIcon from "@mui/icons-material/Apartment";
import SearchIcon from "@mui/icons-material/Search";
import React from "react";
import BuildingDialog from "../Dialogs/BuildingDialog";
import { Util } from "../Helpers/Util";
import { useRole, isAdminRole } from "../Contexts/RoleContext";
import { colors } from "../theme/tokens";

export interface Building {
	Name: string;
	ID: string;
	Address1: string;
	Address2: string;
	City: string;
	State: string;
	Zip: string;
	Active: boolean;
	PrimaryContact: string;
	Latitude?: number | null;
	Longitude?: number | null;
}

interface BuildingListProps {
	selectBuilding: Function;
	defaultBuilding: string | undefined;
	hide: Function;
	selectedBuildingId?: string;
}

export default function BuildingList(props: BuildingListProps) {
	const [buildings, setBuildings] = React.useState([] as Array<Building>);
	const [nameFilter, setNameFilter] = React.useState("");
	const [addNew, setAddNew] = React.useState(false);
	const [filter, setFilter] = React.useState<Boolean | null>(true);
	const { effectiveRole } = useRole();

	React.useEffect(() => {
		getBuildings();
	}, [props.defaultBuilding, addNew, filter, effectiveRole]);

	const getBuildings = () => {
		fetch(isAdminRole(effectiveRole) ? "/Building/GetBuildingsList" : "/Building/GetClientPortalBuildings", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ filter: filter }),
		})
			.then((x) => x.json())
			.then((x) => {
				const hadBuildings = buildings.length > 0;
				const bs = x
					.sort((a: any, b: any) => a.building_name.localeCompare(b.building_name))
					.map((y: any) => {
						return { Name: y.building_name, ID: y.buildings_id } as Building;
					});
				setBuildings(bs);

				if (!hadBuildings) {
					if (x.length === 1) {
						const y = x[0];
						props.hide();
						selectBuilding({ Name: y.building_name, ID: y.buildings_id } as Building);
					} else if (props.defaultBuilding) {
						selectBuilding(bs.find((b: Building) => b.ID === props.defaultBuilding));
					} else {
						const y = x[0];
						selectBuilding({ Name: y.building_name, ID: y.buildings_id } as Building);
					}
				}
			});
	};

	const selectBuilding = (b: Building) => {
		props.selectBuilding(b);
	};

	const filteredBuildings = buildings.filter((x) => {
		return Util.isNullOrEmpty(nameFilter.trim()) ? true : x.Name.toLocaleLowerCase().includes(nameFilter.toLocaleLowerCase());
	});

	return (
		<>
			<BuildingDialog
				open={addNew}
				close={(bid: string) => {
					setAddNew(false);
					if (bid) props.selectBuilding({ ID: bid } as Building);
				}}
			/>
			<Sheet
				variant="outlined"
				sx={{
					p: 1.5,
					borderRadius: "12px",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					gap: 1.5,
					borderColor: colors.border.default,
					boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
				}}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<ApartmentIcon sx={{ color: colors.primary[700], fontSize: 22 }} />
					<Typography level="title-md" sx={{ fontWeight: 700, color: colors.text.primary, flex: 1 }}>
						Buildings
					</Typography>
				</Box>

				<FormControl>
					<Input
						size="sm"
						name="name"
						placeholder="Search buildings..."
						startDecorator={<SearchIcon sx={{ fontSize: 18, color: colors.text.tertiary }} />}
						endDecorator={<SearchIcon sx={{ fontSize: 18, color: colors.text.tertiary }} />}
						onChange={(event) => setNameFilter(event.target.value)}
						sx={{
							borderRadius: "8px",
							"--Input-focusedThickness": "1px",
						}}
					/>
				</FormControl>

				{isAdminRole(effectiveRole) && (
					<>
						<Select size="sm" defaultValue={filter} onChange={(event, value) => setFilter(value)} sx={{ borderRadius: "8px" }}>
							<Option value={true}>Show Active</Option>
							<Option value={false}>Show Inactive</Option>
							<Option value={null}>Show All</Option>
						</Select>
						<Button fullWidth color="primary" startDecorator={<AddIcon />} onClick={() => setAddNew(true)}>
							New Building
						</Button>
					</>
				)}

				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, overflowY: "auto", flex: 1, minHeight: 0 }}>
					{filteredBuildings.map((x) => {
						const isSelected = props.selectedBuildingId === x.ID;
						return (
							<Box
								key={x.ID}
								onClick={() => selectBuilding(x)}
								sx={{
									px: 1.5,
									py: 1,
									borderRadius: "8px",
									cursor: "pointer",
									backgroundColor: isSelected ? colors.buildingList.selected : "transparent",
									color: isSelected ? colors.buildingList.selectedText : colors.text.primary,
									fontWeight: isSelected ? 600 : 400,
									fontSize: "0.875rem",
									lineHeight: 1.4,
									transition: "background-color 0.15s ease, color 0.15s ease",
									"&:hover": {
										backgroundColor: isSelected ? colors.buildingList.selected : colors.buildingList.hover,
									},
								}}>
								{x.Name}
							</Box>
						);
					})}
				</Box>
			</Sheet>
		</>
	);
}
